import AddOutlined from "@mui/icons-material/AddOutlined";
import ArrowForwardOutlined from "@mui/icons-material/ArrowForwardOutlined";
import CampaignOutlined from "@mui/icons-material/CampaignOutlined";
import ChevronRightOutlined from "@mui/icons-material/ChevronRightOutlined";
import CodeOutlined from "@mui/icons-material/CodeOutlined";
import HeadsetMicOutlined from "@mui/icons-material/HeadsetMicOutlined";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import PeopleAltOutlined from "@mui/icons-material/PeopleAltOutlined";
import ShoppingBagOutlined from "@mui/icons-material/ShoppingBagOutlined";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import type { SxProps } from "@mui/material/styles";
import { CAAC_URL, DAAC_URL, MAAC_URL } from "@polifonia/env";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { RouterLink } from "@/components/RouterLink";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import { useOrgChannels } from "@/features/channels/useOrgChannels";
import { trackGaEvent } from "@/features/ga/trackGaEvent";
import { useEnableMetaAdsIntegration } from "@/features/meta-ads/useEnableMetaAdsIntegration";
import { useEnableOpenApiV2KeySelfServe } from "@/features/openapi/useEnableOpenApiV2KeySelfServe";
import { UnifyKeyLabel } from "@/features/unify-scope-setting/UnifyKeyLabel";
import { interlude } from "@/interlude";
import { ECOMMERCE_PLATFORMS } from "@/pages/applications/ecommercePlatforms";
import { ChannelIcon } from "@/pages/channels/components/ChannelIcon";

/** Below this width the three columns stack and the arrows point down. */
const STACKED = "@media (max-width: 900px)";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px 24px",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "12px",
    backgroundColor: theme.colors.white,
  },
  flow: {
    display: "grid",
    gridTemplateColumns:
      "minmax(0, 1fr) 24px minmax(0, 0.8fr) 24px minmax(0, 1fr)",
    // Top-aligned so the hub and destinations do not sink to the middle of a
    // long source list and leave the top right empty.
    alignItems: "start",
    gap: "12px",
    [STACKED]: {
      gridTemplateColumns: "minmax(0, 1fr)",
    },
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  columnHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "8px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minHeight: "40px",
    padding: "8px 12px",
    borderRadius: "8px",
    backgroundColor: theme.colors.neutral010,
  },
  linkRow: {
    color: theme.colors.staticFgBody,
    textDecoration: "none",
    transition: "background-color 0.15s",
    "&:hover": {
      backgroundColor: theme.colors.staticBgPrimary,
      "& .trailing": { color: theme.colors.blue060 },
    },
  },
  offerRow: {
    backgroundColor: theme.colors.white,
    border: `1px dashed ${theme.colors.neutral050}`,
  },
  rowIcon: {
    display: "inline-flex",
    "& svg": {
      fontSize: "1.125rem",
      color: theme.colors.staticFgNote,
    },
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  trailing: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    color: theme.colors.staticFgNote,
    "& svg": { fontSize: "1.125rem" },
  },
  more: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginTop: "4px",
    paddingTop: "12px",
    borderTop: `1px solid ${theme.colors.staticFgLine}`,
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 12px 4px 8px",
    borderRadius: "999px",
    border: `1px dashed ${theme.colors.blue030}`,
    color: theme.colors.blue060,
    fontSize: "0.8125rem",
    textDecoration: "none",
    "& svg": { fontSize: "1rem" },
    "&:hover": {
      backgroundColor: theme.colors.staticBgPrimary,
      textDecoration: "none",
    },
  },
  arrow: {
    display: "flex",
    justifyContent: "center",
    // Lines up with the middle of the hub card.
    paddingTop: "88px",
    color: theme.colors.neutral060,
    [STACKED]: {
      paddingTop: "0px",
      transform: "rotate(90deg)",
    },
  },
  hub: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    // Clears the column labels beside it.
    marginTop: "28px",
    padding: "20px 16px",
    borderRadius: "12px",
    border: `1px solid ${theme.colors.blue020}`,
    backgroundColor: theme.colors.staticBgPrimary,
    textAlign: "center",
    [STACKED]: {
      marginTop: "0px",
    },
  },
  hubIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.colors.blue060,
    color: theme.colors.white,
  },
  keys: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "4px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const Row: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
  trailing?: React.ReactNode;
}> = function Row({ icon, children, trailing }) {
  return (
    <Box sx={styles.row}>
      <Box component="span" sx={styles.rowIcon}>
        {icon}
      </Box>
      <Box sx={styles.rowText}>
        <Typography variant="body">{children}</Typography>
      </Box>
      {trailing}
    </Box>
  );
};

type InAppDestination =
  | "/applications"
  | "/applications/api-keys"
  | "/applications/mcp-connections";

/**
 * A place unified contacts are used, as a row that opens it: another product
 * in a new tab, or a page of this app. `offer` marks one the org may not have
 * yet, drawn dashed so it never reads as in use.
 */
const DestinationRow: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
  destination: { href: string } | { to: InAppDestination };
  /** Reported with the click, e.g. "maac". */
  trackingKey: string;
  offer?: React.ReactNode;
}> = function DestinationRow({
  icon,
  children,
  destination,
  trackingKey,
  offer,
}) {
  const sx = {
    ...styles.row,
    ...styles.linkRow,
    ...(offer ? styles.offerRow : null),
  };
  const content = (
    <>
      <Box component="span" sx={styles.rowIcon}>
        {icon}
      </Box>
      <Box sx={styles.rowText}>
        <Typography variant="body">{children}</Typography>
      </Box>
      <Box component="span" className="trailing" sx={styles.trailing}>
        {offer ? <Typography variant="note">{offer}</Typography> : null}
        {"href" in destination ? (
          <OpenInNewOutlined />
        ) : (
          <ChevronRightOutlined />
        )}
      </Box>
    </>
  );
  const handleClick = () =>
    trackGaEvent("dataFlowDestinationClick", { label: trackingKey });

  return "href" in destination ? (
    <Link
      href={destination.href}
      target="_blank"
      rel="noopener noreferrer"
      underline="none"
      sx={sx}
      onClick={handleClick}
    >
      {content}
    </Link>
  ) : (
    <RouterLink
      to={destination.to}
      underline="none"
      sx={sx}
      onClick={handleClick}
    >
      {content}
    </RouterLink>
  );
};

/** Channel types in the order the Channels page groups them, with a label. */
const useChannelGroups = () => {
  const orgChannels = useOrgChannels();
  return useMemo(() => {
    if (!orgChannels.isSuccess) return null;
    const counts = new Map<
      (typeof orgChannels.channels)[number]["type"],
      number
    >();
    for (const channel of orgChannels.channels) {
      counts.set(channel.type, (counts.get(channel.type) ?? 0) + 1);
    }
    return [...counts.entries()].map(([type, count]) => ({ type, count }));
  }, [orgChannels]);
};

/** The sources most brands connect, suggested when missing. */
const SUGGESTED_CHANNELS = ["line", "fb", "ig", "wccs"] as const;
type SuggestedChannel = (typeof SUGGESTED_CHANNELS)[number];

/** Readable channel type names; brand names stay untranslated. */
const channelTypeNames: Record<string, string> = {
  line: "LINE",
  fb: "Facebook",
  ig: "Instagram",
  whatsapp: "WhatsApp",
  wccs: "Web",
  email: "Email",
  sms: "SMS",
};

/**
 * Where the org's customer data comes from and where unified profiles are
 * used, drawn from what is connected today. A destination is listed as in use
 * only when it is; nothing is shown as connected that is not.
 */
export const DataFlow: React.FC = function DataFlow() {
  const { t } = useTranslation();
  const channelBenefits: Record<SuggestedChannel, string> = {
    line: t(
      "dataHub.flow.missing.line",
      "Every LINE friend becomes a customer profile",
    ),
    fb: t(
      "dataHub.flow.missing.fb",
      "People who comment or message on Facebook join the same profile",
    ),
    ig: t(
      "dataHub.flow.missing.ig",
      "People who comment or message on Instagram join the same profile",
    ),
    wccs: t(
      "dataHub.flow.missing.web",
      "See which products customers browse on your website",
    ),
  };
  const channelGroups = useChannelGroups();
  const unifyScopeSettingQuery =
    interlude.unifyScopeSetting.useGetUnifyScopeSetting();
  const meQuery = interlude.auth.useGetMe();
  // The API keys page has its own gate and sends everyone else back to
  // /applications, so it is linked only when that gate is on.
  const canOpenApiKeysPage = useEnableOpenApiV2KeySelfServe();
  const hasEcModuleQuery = interlude.organization.useHasEcModule();
  const organizationSettingQuery =
    interlude.organization.useOrganizationSetting();
  const googleAdsQuery = interlude.googleAds.useGetManagerAccount({
    queries: { checkAccess: false },
  });
  const enableMetaAds = useEnableMetaAdsIntegration();
  const metaAdsStatusQuery = interlude.metaAds.useGetStatus(undefined, {
    enabled: enableMetaAds,
  });
  const lineAdsGroupQuery = interlude.lineAds.useGetGroup({
    queries: { checkAccess: false },
  });

  if (!unifyScopeSettingQuery.isSuccess) return null;

  const scope = unifyScopeSettingQuery.data;
  const hasMaac = scope.orgs.some((org) => org.source === "maac");
  const hasCaac = scope.orgs.some((org) => org.source === "caac");
  // The platform has no API for an org's products; only a user who arrived
  // from DAAC tells us the org has it.
  const usesDaac = meQuery.data?.source === "daac";
  const ecPlatforms = (hasEcModuleQuery.data?.ecPlatforms ?? []).map(
    (type) =>
      ECOMMERCE_PLATFORMS.find((platform) => platform.type === type)?.name ??
      type,
  );
  const adPlatforms = [
    googleAdsQuery.isSuccess ? "Google Ads" : null,
    metaAdsStatusQuery.data?.tokenStatus === "active" ? "Meta Ads" : null,
    lineAdsGroupQuery.isSuccess ? "LINE Ads" : null,
  ].filter((name): name is string => name !== null);
  const openApiEnabled = Boolean(
    organizationSettingQuery.data?.openApiV2Enabled,
  );
  const apiAccess = [
    openApiEnabled ? "OpenAPI" : null,
    organizationSettingQuery.data?.mcpEnabled ? "MCP" : null,
  ].filter((name): name is string => name !== null);

  const connectedTypes = new Set(channelGroups?.map((group) => group.type));
  // Suggested only once the channel list has loaded, so a slow request never
  // shows a connected channel as missing.
  const missingSources: Array<{
    key: string;
    name: string;
    benefit: string;
    to: "/channels" | "/applications";
  }> = channelGroups
    ? [
        ...SUGGESTED_CHANNELS.filter((type) => !connectedTypes.has(type)).map(
          (type) => ({
            key: type,
            name: channelTypeNames[type] ?? type,
            benefit: channelBenefits[type],
            to: "/channels" as const,
          }),
        ),
        // Only when the platform list loaded and is empty: an older interlude
        // response without the list says nothing either way.
        ...(hasEcModuleQuery.data?.ecPlatforms?.length === 0
          ? [
              {
                key: "ecommerce",
                name: t(
                  "dataHub.sources.ecommerce.title",
                  "E-commerce platforms",
                ),
                benefit: t(
                  "dataHub.flow.missing.ecommerce",
                  "Bring purchase totals into each customer's profile",
                ),
                to: "/applications" as const,
              },
            ]
          : []),
      ]
    : [];
  // POS and CRM data arrive through the API, which the platform cannot see,
  // so they are always offered rather than marked connected or missing.
  const apiSourcesBenefit = `${t(
    "dataHub.flow.more.posBenefit",
    "Add offline orders to the same customer through the Commerce Data API",
  )} ${t(
    "dataHub.flow.more.crmBenefit",
    "Match members by Customer ID across every channel",
  )}`;
  const apiSourcesLabel = (
    <>
      <CodeOutlined />
      {t("dataHub.flow.apiChip", "POS · CRM (API)")}
    </>
  );

  return (
    <Box component="section" sx={styles.root}>
      <Typography variant="h3" fontWeight={700}>
        {t("dataHub.flow.title", "How your customer data flows")}
      </Typography>
      <Box sx={styles.flow}>
        <Box sx={styles.column}>
          <Box sx={styles.columnHeader}>
            <Typography variant="note" color={theme.colors.staticFgNote}>
              {t("dataHub.flow.sources", "Comes from")}
            </Typography>
            <RouterLink
              to="/data-hub"
              search={{ tab: "sources" }}
              variant="body2"
              aria-label={t("dataHub.sources.view", "View data sources")}
            >
              {t("common.manage", "Manage")}
            </RouterLink>
          </Box>
          {channelGroups?.map((group) => (
            <Row
              key={group.type}
              icon={<ChannelIcon type={group.type} />}
              trailing={
                <Typography variant="note" color={theme.colors.staticFgNote}>
                  {t("dataHub.sources.channelCount", {
                    count: group.count,
                    defaultValue: "{{count}} channels",
                  })}
                </Typography>
              }
            >
              {channelTypeNames[group.type] ?? group.type}
            </Row>
          ))}
          {ecPlatforms.length > 0 ? (
            <Row icon={<ShoppingBagOutlined />}>{ecPlatforms.join(", ")}</Row>
          ) : null}
          <Box sx={styles.more}>
            <Typography variant="note" color={theme.colors.staticFgNote}>
              {t("dataHub.flow.alsoConnect", "Also connect")}
            </Typography>
            <Box sx={styles.chips}>
              {missingSources.map((source) => (
                <Tooltip
                  key={source.key}
                  title={source.benefit}
                  arrow
                  describeChild
                >
                  <RouterLink
                    to={source.to}
                    sx={styles.chip}
                    onClick={() =>
                      trackGaEvent("dataFlowConnectClick", {
                        label: source.key,
                      })
                    }
                  >
                    <AddOutlined />
                    {source.name}
                  </RouterLink>
                </Tooltip>
              ))}
              <Tooltip title={apiSourcesBenefit} arrow describeChild>
                {openApiEnabled && canOpenApiKeysPage ? (
                  <RouterLink
                    to="/applications/api-keys"
                    sx={styles.chip}
                    onClick={() =>
                      trackGaEvent("dataFlowApiSourceClick", {
                        label: "openapi",
                      })
                    }
                  >
                    {apiSourcesLabel}
                  </RouterLink>
                ) : (
                  <Link
                    href={t("newRequest.link")}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={styles.chip}
                    onClick={() =>
                      trackGaEvent("dataFlowApiSourceClick", {
                        label: "contact",
                      })
                    }
                  >
                    {apiSourcesLabel}
                  </Link>
                )}
              </Tooltip>
            </Box>
            <Typography variant="note" color={theme.colors.staticFgNote}>
              {t(
                "dataHub.flow.alsoConnectHint",
                "The more you connect, the more complete each contact's data",
              )}
            </Typography>
          </Box>
        </Box>
        <Box sx={styles.arrow} aria-hidden="true">
          <ArrowForwardOutlined />
        </Box>
        <Box sx={styles.hub}>
          <Box sx={styles.hubIcon}>
            <PeopleAltOutlined />
          </Box>
          <Typography variant="body" fontWeight={600}>
            {t("dataHub.flow.hub", "One profile per customer")}
          </Typography>
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t("dataHub.flow.mergedBy", "Merged by")}
          </Typography>
          <Box sx={styles.keys}>
            {scope.unifyKeys.map((key) => (
              <Badge key={key.attribute} variant="plain">
                <UnifyKeyLabel unifyKey={key.attribute} />
              </Badge>
            ))}
          </Box>
          <RouterLink to="/data-hub" search={{ tab: "rules" }} variant="body2">
            {t("dataHub.rules.edit", "Edit merge rules")}
          </RouterLink>
        </Box>
        <Box sx={styles.arrow} aria-hidden="true">
          <ArrowForwardOutlined />
        </Box>
        <Box sx={styles.column}>
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t("dataHub.flow.destinations", "Used in")}
          </Typography>
          {hasMaac && MAAC_URL ? (
            <DestinationRow
              icon={<CampaignOutlined />}
              destination={{ href: MAAC_URL }}
              trackingKey="maac"
            >
              {t("dataHub.flow.maac", "MAAC segments, broadcasts and journeys")}
            </DestinationRow>
          ) : null}
          {hasCaac && CAAC_URL ? (
            <DestinationRow
              icon={<HeadsetMicOutlined />}
              destination={{ href: CAAC_URL }}
              trackingKey="caac"
            >
              {t("dataHub.flow.caac", "CAAC customer view in conversations")}
            </DestinationRow>
          ) : null}
          {DAAC_URL ? (
            <DestinationRow
              icon={<InsightsOutlined />}
              destination={{ href: DAAC_URL }}
              trackingKey="daac"
              offer={
                usesDaac
                  ? undefined
                  : t("userManagement.products.goToDaac", "Go to DAAC")
              }
            >
              {t("dataHub.flow.daac", "DAAC audience insights and personas")}
            </DestinationRow>
          ) : null}
          {adPlatforms.length > 0 ? (
            <DestinationRow
              icon={<CampaignOutlined />}
              destination={{ to: "/applications" }}
              trackingKey="ads"
            >
              {t("dataHub.flow.ads", {
                platforms: adPlatforms.join(", "),
                defaultValue: "Ad audiences: {{platforms}}",
              })}
            </DestinationRow>
          ) : null}
          {apiAccess.length > 0 ? (
            <DestinationRow
              icon={<CodeOutlined />}
              destination={{
                to:
                  openApiEnabled && canOpenApiKeysPage
                    ? "/applications/api-keys"
                    : "/applications/mcp-connections",
              }}
              trackingKey="api"
            >
              {t("dataHub.flow.api", {
                tools: apiAccess.join(" & "),
                defaultValue: "Your systems and AI via {{tools}}",
              })}
            </DestinationRow>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
